import { computed, toValue } from 'vue'
import { useRoute } from 'nuxt/app'
import { useI18n } from 'vue-i18n'

import { createElementId } from '@common/core-layer/utils'

import type { CPBreadcrumbItem } from '@ui/components/types/breadcrumb.types'

import { getCpBreadcrumbDefinition, resolveCpBreadcrumbRegistryKey } from '../config/cp-breadcrumb-registry'
import { useCPNavigation } from './useNavigationContext'
import { injectCommunitiesGroupNameAsFinalSegment } from '../utils/inject-communities-group-breadcrumbs'
import { injectCoursesProductNameAsFinalSegment, injectCoursesProductWithGroupContext } from '../utils/inject-courses-product-breadcrumbs'
import { isCourseProductRouteKey } from '../utils/course-route-classification'
import type { CpBreadcrumbPageMeta, UseCPBreadcrumbsOptions } from '../types/cp-breadcrumb'

/**
 * Builds `CPBreadcrumbItem[]` for the Client Portal top bar from the central registry,
 * optional `route.meta.cpBreadcrumb`, and optional shell context.
 */
export function useCPBreadcrumbs(options?: UseCPBreadcrumbsOptions) {
  const route = useRoute()
  const { t } = useI18n()
  const { CPNavigateTo } = useCPNavigation()

  const breadcrumbs = computed((): CPBreadcrumbItem[] | null => {
    if (options?.enabled !== undefined && !toValue(options.enabled)) {
      return null
    }

    const key = resolveCpBreadcrumbRegistryKey(route)
    if (!key) {
      return null
    }

    const def = getCpBreadcrumbDefinition(key)
    if (!def?.segments?.length) {
      return null
    }

    let items: CPBreadcrumbItem[] = []

    for (let i = 0; i < def.segments.length; i++) {
      const seg = def.segments[i]
      const isLast = i === def.segments.length - 1
      const label = t(seg.labelKey)

      if (isLast) {
        items.push({ label })
        continue
      }

      const navInput = seg.navigate?.(route)
      if (navInput) {
        items.push({
          label,
          onClick: () => {
            if (seg.forceDefaultTemplate) {
              void CPNavigateTo(navInput, true)
            } else {
              void CPNavigateTo(navInput)
            }
          },
        })
      } else {
        items.push({ label })
      }
    }

    let truncatedToGroupName = false
    const groupCtx = toValue(options?.communitiesGroup)
    if (groupCtx && key.startsWith('communities-groups-') && groupCtx.groupSlug && groupCtx.groupName) {
      items = injectCommunitiesGroupNameAsFinalSegment(items, groupCtx.groupName, () => {
        void CPNavigateTo({
          name: 'communities-groups-home',
          params: { groupSlug: groupCtx.groupSlug },
        })
      })
      truncatedToGroupName = true
    }

    let truncatedToCourseName = false
    const courseCtx = toValue(options?.coursesProduct)
    if (courseCtx && courseCtx.productId && courseCtx.courseName && isCourseProductRouteKey(key)) {
      const params: Record<string, string> = { productId: courseCtx.productId }
      const linkFinalToProduct = key !== 'courses-products'
      const trailingItem = key === 'courses-products-live-sessions' ? items.at(-1) : undefined
      const navigateToProduct = () => {
        void CPNavigateTo(
          {
            name: 'courses-products',
            params,
            ...(courseCtx.groupSlug ? { query: { groupSlug: courseCtx.groupSlug } } : {}),
          },
          true
        )
      }

      if (courseCtx.groupSlug && courseCtx.groupName) {
        items = injectCoursesProductWithGroupContext(items, {
          groupName: courseCtx.groupName,
          courseName: courseCtx.courseName,
          linkFinalToProduct,
          onNavigateToGroup: () => {
            void CPNavigateTo({
              name: 'communities-groups-learning',
              params: { groupSlug: courseCtx.groupSlug! },
            })
          },
          onNavigateToProduct: navigateToProduct,
        })
      } else {
        items = injectCoursesProductNameAsFinalSegment(items, courseCtx.courseName, {
          linkFinalToProduct,
          onNavigateToProduct: navigateToProduct,
        })
      }
      if (trailingItem) items.push(trailingItem)
      truncatedToCourseName = true
    }

    const meta = route.meta.cpBreadcrumb as CpBreadcrumbPageMeta | undefined
    const metaLast = meta?.lastLabel && meta.lastLabel.length > 0 ? meta.lastLabel : undefined

    if (!truncatedToGroupName && !truncatedToCourseName && metaLast && items.length > 0) {
      const last = items[items.length - 1]
      items[items.length - 1] = { ...last, label: metaLast }
    }

    const appOverride = toValue(options?.lastSegmentLabel)
    if (!truncatedToGroupName && !truncatedToCourseName && appOverride && items.length > 0) {
      if (key === 'clientportal-exhibitor-center' && items.length >= 2) {
        const eventCrumb = items[items.length - 2]
        items[items.length - 2] = { ...eventCrumb, label: appOverride }
      } else {
        const last = items[items.length - 1]
        items[items.length - 1] = { ...last, label: appOverride }
      }
    }

    return items
  })

  const breadcrumbId = computed(() => createElementId('cp', 'topbar', 'breadcrumb'))

  return {
    breadcrumbs,
    breadcrumbId,
    separator: '>',
  }
}
